// Find every external reference and record whether it is pinned.
//
// FM-04 is the failure mode this feeds: a contract bound to "the latest version
// in effect at the time of award" of a document nobody recorded. So for each
// citation the question is not only what was cited but whether a version and a
// date were given.

import type { Citation } from '../../types';
import type { PageLayout } from './pdfExtract';

const STANDARDS: { name: string; pattern: RegExp }[] = [
  { name: 'FEMP M&V Guidelines', pattern: /FEMP\s+M&V\s+Guideline?s?/gi },
  { name: 'IPMVP', pattern: /\bIPMVP\b/g },
  { name: 'ASHRAE Guideline 14', pattern: /ASHRAE\s+Guideline\s*14/gi },
  { name: '10 CFR 436', pattern: /10\s*C\.?F\.?R\.?\s*(?:Part\s*)?436/gi },
  { name: '42 U.S.C. 8287', pattern: /42\s*U\.?S\.?C\.?\s*(?:§+\s*)?8287/gi },
];

/** FEMP method codes, e.g. LE-A-02, GVL-B-01, CH-B-02. */
const METHOD_CODE = /\b[A-Z]{2,3}-[A-D]-\d{2}\b/g;

const URL_PATTERN = /\b(?:https?:\/\/|www\.)[^\s,;)\]]+/gi;

/** Language that defers to whatever was current, without saying what that was. */
const VERSION_PHRASE =
  /\b(?:latest version|most recent version|as amended|current(?:ly)?\s+(?:in\s+)?effect|in effect at the time of|then[- ]current)\b[^.\n]{0,80}/gi;

const VERSION_TOKEN =
  /\b(?:v(?:er(?:sion)?)?\.?\s*\d+(?:\.\d+)*|rev(?:ision)?\.?\s*\d+|\d+\.\d+)\b/i;
const DATE_TOKEN =
  /\b(?:19|20)\d{2}\b|\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+(?:19|20)?\d{2}\b/i;

const PLACEHOLDER_MARKERS = ['…', '...', 'www…', 'TBD', 'tbd', '[', 'XXX'];
const PLACEHOLDER_MIN_CHARS = 15;

/**
 * The under-15-characters test applies only to URLs and free-text references.
 * A method code ("LE-A-02") and a standard's name ("IPMVP") are legitimately
 * short and complete; marking all nine method codes in a plan as placeholders
 * would bury the one reference that really is a stub.
 */
function isPlaceholder(raw: string, kind: Citation['kind']): boolean {
  const text = raw.trim();
  const lengthApplies = kind === 'url' || kind === 'internal';
  if (lengthApplies && text.length < PLACEHOLDER_MIN_CHARS) return true;
  return PLACEHOLDER_MARKERS.some((m) => text.includes(m));
}

/**
 * Context for the version and date test. It must cross line boundaries: this
 * plan cites "FEMP M&V Guidelines: Measurement & Verification for Federal
 * Energy" on one line and "Projects, Version 2." on the next, and a
 * line-scoped window would report a pinned citation as unpinned.
 */
const CONTEXT_RADIUS = 160;

function contextOf(pageText: string, absoluteIndex: number, length: number): string {
  const from = Math.max(0, absoluteIndex - CONTEXT_RADIUS);
  const to = Math.min(pageText.length, absoluteIndex + length + CONTEXT_RADIUS);
  return pageText.slice(from, to).replace(/\s+/g, ' ').trim();
}

export function scanCitations(pages: PageLayout[]): Citation[] {
  const citations: Citation[] = [];
  const seen = new Set<string>();
  let counter = 0;

  const push = (
    raw: string,
    page: number,
    kind: Citation['kind'],
    context: string,
    resolvedUrl?: string,
  ) => {
    const key = `${kind}::${raw.trim().toLowerCase()}::${page}`;
    if (seen.has(key)) return;
    seen.add(key);

    citations.push({
      id: `citation-${++counter}`,
      raw: raw.trim(),
      page,
      kind,
      // A version or date anywhere in the surrounding context counts as given.
      versionGiven: VERSION_TOKEN.test(context),
      dateGiven: DATE_TOKEN.test(context),
      resolvedUrl,
      placeholder: isPlaceholder(raw, kind),
    });
  };

  for (const page of pages) {
    // The page as one string, plus each line's offset within it, so a match
    // index becomes a position in the page and the context window can span
    // lines.
    const pageText = page.lines.map((l) => l.text).join('\n');
    let lineOffset = 0;

    for (const line of page.lines) {
      const text = line.text.replace(/\t/g, ' ');
      const offset = lineOffset;
      lineOffset += line.text.length + 1;
      if (!text.trim()) continue;
      // Dot leaders in a contents listing are not citations.
      if (/\.{4,}/.test(text)) continue;

      for (const { pattern } of STANDARDS) {
        pattern.lastIndex = 0;
        let m: RegExpExecArray | null;
        while ((m = pattern.exec(text)) !== null) {
          push(m[0], page.number, 'standard', contextOf(pageText, offset + m.index, m[0].length));
        }
      }

      METHOD_CODE.lastIndex = 0;
      let code: RegExpExecArray | null;
      while ((code = METHOD_CODE.exec(text)) !== null) {
        push(
          code[0],
          page.number,
          'method-code',
          contextOf(pageText, offset + code.index, code[0].length),
        );
      }

      URL_PATTERN.lastIndex = 0;
      let url: RegExpExecArray | null;
      while ((url = URL_PATTERN.exec(text)) !== null) {
        const raw = url[0].replace(/[.,;]+$/, '');
        push(
          raw,
          page.number,
          'url',
          contextOf(pageText, offset + url.index, raw.length),
          raw.startsWith('http') ? raw : `http://${raw}`,
        );
      }

      VERSION_PHRASE.lastIndex = 0;
      let phrase: RegExpExecArray | null;
      while ((phrase = VERSION_PHRASE.exec(text)) !== null) {
        push(
          phrase[0],
          page.number,
          'internal',
          contextOf(pageText, offset + phrase.index, phrase[0].length),
        );
      }

      // A footnote or reference stub that resolves to nothing: short, or built
      // out of ellipses. "PSG&E table…www…" is the case this exists for.
      const stub = text.trim();
      if (
        stub.length <= 60 &&
        PLACEHOLDER_MARKERS.some((mk) => stub.includes(mk)) &&
        /[A-Za-z]{3,}/.test(stub) &&
        !URL_PATTERN.test(stub)
      ) {
        push(stub, page.number, 'internal', stub);
      }
    }
  }

  return citations;
}
